import React, { useState, useCallback, useRef } from 'react';
import { Popup } from 'devextreme-react/popup';
import { Button } from 'devextreme-react/button';
import { CheckBox } from 'devextreme-react/check-box';
import { FileUploader } from 'devextreme-react/file-uploader';
import { ScrollView } from 'devextreme-react/scroll-view';
import { TagBox } from 'devextreme-react/tag-box';
import { LoadPanel } from 'devextreme-react/load-panel';
import notify from 'devextreme/ui/notify';
import { analyzeCompetitionFile, importLenex, importDSV } from '../../utils/competitionImportExportApi';
import './CompetitionImportDialog.scss';

export default function CompetitionImportDialog({ visible, onHiding, onImportComplete, token, competitionId }) {
    const [step, setStep] = useState(1); // 1 = Upload, 2 = Options
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileAnalysis, setFileAnalysis] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingText, setLoadingText] = useState('');
    
    // Import Options
    const [importCompetitionData, setImportCompetitionData] = useState(true);
    const [importEvents, setImportEvents] = useState(true);
    const [importEntries, setImportEntries] = useState(true);
    const [importResults, setImportResults] = useState(false);
    const [selectedClubIds, setSelectedClubIds] = useState([]);
    const [selectedEventNumbers, setSelectedEventNumbers] = useState([]);

    const fileUploaderRef = useRef(null);

    const resetDialog = useCallback(() => {
        setStep(1);
        setSelectedFile(null);
        setFileAnalysis(null);
        setImportCompetitionData(true);
        setImportEvents(true);
        setImportEntries(true);
        setImportResults(false);
        setSelectedClubIds([]);
        setSelectedEventNumbers([]);
    }, []);

    const handleHiding = useCallback(() => {
        resetDialog();
        onHiding();
    }, [resetDialog, onHiding]);

    const handleFileSelected = useCallback((e) => {
        const files = e.value;
        if (files && files.length > 0) {
            setSelectedFile(files[0]);
        }
    }, []);

    const handleAnalyze = useCallback(async () => {
        if (!selectedFile) {
            notify('Bitte wählen Sie zuerst eine Datei aus', 'warning', 3000);
            return;
        }

        setIsLoading(true);
        setLoadingText('Datei wird analysiert...');

        try {
            const result = await analyzeCompetitionFile(selectedFile, token);
            
            if (result.success) {
                setFileAnalysis(result);
                
                // Alle Vereine und Events vorselektieren
                if (result.content?.availableClubs) {
                    setSelectedClubIds(result.content.availableClubs.map(c => c.id));
                }
                if (result.content?.availableEvents) {
                    setSelectedEventNumbers(result.content.availableEvents.map(e => e.number));
                }
                
                setStep(2);
                notify(`Datei erfolgreich analysiert: ${result.content?.totalClubs || 0} Vereine, ${result.content?.totalEvents || 0} Wettkämpfe`, 'success', 3000);
            } else {
                notify(result.message || 'Fehler beim Analysieren der Datei', 'error', 5000);
            }
        } catch (error) {
            console.error('Error analyzing file:', error);
            notify(error.response?.data?.message || error.message || 'Fehler beim Analysieren der Datei', 'error', 5000);
        } finally {
            setIsLoading(false);
        }
    }, [selectedFile, token]);

    const handleImport = useCallback(async () => {
        if (!fileAnalysis) return;

        setIsLoading(true);
        setLoadingText('Import wird durchgeführt...');

        try {
            const settings = {
                fileId: fileAnalysis.fileId,
                competitionId,
                importCompetitionData,
                importEvents,
                importEntries,
                importResults,
                selectedClubIds: selectedClubIds.length > 0 ? selectedClubIds : undefined,
                selectedEventNumbers: selectedEventNumbers.length > 0 ? selectedEventNumbers : undefined
            };

            let result;
            if (fileAnalysis.fileType === 'Lenex') {
                result = await importLenex(settings, null, token);
            } else {
                result = await importDSV(settings, null, token);
            }

            if (result.success) {
                notify(`Import erfolgreich: ${result.importedClubsCount || 0} Vereine, ${result.importedEventsCount || 0} Wettkämpfe, ${result.importedEntriesCount || 0} Meldungen`, 'success', 5000);
                
                if (result.warnings && result.warnings.length > 0) {
                    notify('Warnungen beim Import: ' + result.warnings.join(', '), 'warning', 5000);
                }
                
                resetDialog();
                onImportComplete(result);
                onHiding();
            } else {
                notify(result.message || 'Fehler beim Import', 'error', 5000);
                if (result.errors && result.errors.length > 0) {
                    notify('Fehler: ' + result.errors.join(', '), 'error', 5000);
                }
            }
        } catch (error) {
            console.error('Error importing file:', error);
            notify(error.response?.data?.message || error.message || 'Fehler beim Import', 'error', 5000);
        } finally {
            setIsLoading(false);
        }
    }, [fileAnalysis, importCompetitionData, importEvents, importEntries, importResults, selectedClubIds, selectedEventNumbers, token, resetDialog, onImportComplete, onHiding]);

    const handleBack = useCallback(() => {
        setStep(1);
    }, []);

    const renderStepContent = () => {
        if (step === 1) {
            return (
                <div className="import-step-upload">
                    <h3>Datei auswählen</h3>
                    <p>Wählen Sie eine Lenex (.lxf, .xml) oder DSV (.txt) Datei aus:</p>
                    
                    <FileUploader
                        ref={fileUploaderRef}
                        selectButtonText="Datei auswählen"
                        labelText="oder Datei hierher ziehen"
                        accept=".lxf,.xml,.txt"
                        uploadMode="useForm"
                        onValueChanged={handleFileSelected}
                        maxFileSize={10000000}
                    />
                    
                    {selectedFile && (
                        <div className="selected-file-info">
                            <strong>Ausgewählte Datei:</strong> {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
                        </div>
                    )}
                </div>
            );
        }

        if (step === 2 && fileAnalysis) {
            const { content } = fileAnalysis;
            
            return (
                <ScrollView className="import-step-options">
                    <h3>Import-Optionen</h3>
                    
                    <div className="analysis-summary">
                        <h4>Datei-Analyse:</h4>
                        <ul>
                            <li><strong>Dateityp:</strong> {fileAnalysis.fileType}</li>
                            <li><strong>Wettkampf:</strong> {content.competition?.name}</li>
                            {content.competition?.city && <li><strong>Ort:</strong> {content.competition.city}</li>}
                            {content.competition?.startDate && <li><strong>Datum:</strong> {content.competition.startDate} - {content.competition.endDate}</li>}
                            <li><strong>Vereine:</strong> {content.totalClubs}</li>
                            <li><strong>Athleten:</strong> {content.totalAthletes}</li>
                            <li><strong>Wettkämpfe:</strong> {content.totalEvents}</li>
                            <li><strong>Meldungen:</strong> {content.totalEntries}</li>
                            <li><strong>Ergebnisse:</strong> {content.totalResults}</li>
                        </ul>
                    </div>

                    <div className="import-options">
                        <h4>Was soll importiert werden?</h4>
                        
                        <CheckBox
                            text="Wettkampf-Daten"
                            value={importCompetitionData}
                            onValueChanged={(e) => setImportCompetitionData(e.value)}
                        />
                        
                        <CheckBox
                            text="Wettkämpfe/Events"
                            value={importEvents}
                            onValueChanged={(e) => setImportEvents(e.value)}
                        />
                        
                        <CheckBox
                            text="Meldungen"
                            value={importEntries}
                            onValueChanged={(e) => setImportEntries(e.value)}
                        />
                        
                        <CheckBox
                            text="Ergebnisse"
                            value={importResults}
                            onValueChanged={(e) => setImportResults(e.value)}
                        />
                    </div>

                    {content.availableClubs && content.availableClubs.length > 0 && (
                        <div className="club-selection">
                            <h4>Vereine auswählen:</h4>
                            <TagBox
                                dataSource={content.availableClubs}
                                displayExpr="name"
                                valueExpr="id"
                                value={selectedClubIds}
                                onValueChanged={(e) => setSelectedClubIds(e.value)}
                                searchEnabled={true}
                                showSelectionControls={true}
                                selectAllMode="allPages"
                                placeholder="Alle Vereine"
                                itemRender={(item) => (
                                    <div>
                                        <strong>{item.name}</strong> ({item.shortName || item.id})
                                        <span style={{ marginLeft: '10px', color: '#888' }}>
                                            {item.athleteCount} Athleten, {item.entryCount} Meldungen
                                        </span>
                                    </div>
                                )}
                            />
                        </div>
                    )}

                    {content.availableEvents && content.availableEvents.length > 0 && (
                        <div className="event-selection">
                            <h4>Wettkämpfe auswählen:</h4>
                            <TagBox
                                dataSource={content.availableEvents}
                                displayExpr="name"
                                valueExpr="number"
                                value={selectedEventNumbers}
                                onValueChanged={(e) => setSelectedEventNumbers(e.value)}
                                searchEnabled={true}
                                showSelectionControls={true}
                                selectAllMode="allPages"
                                placeholder="Alle Wettkämpfe"
                                itemRender={(item) => (
                                    <div>
                                        <strong>#{item.number}</strong> - {item.name}
                                        <span style={{ marginLeft: '10px', color: '#888' }}>
                                            {item.entryCount} Meldungen
                                        </span>
                                    </div>
                                )}
                            />
                        </div>
                    )}
                </ScrollView>
            );
        }

        return null;
    };

    return (
        <>
            <Popup
                visible={visible}
                onHiding={handleHiding}
                dragEnabled={false}
                closeOnOutsideClick={false}
                showTitle={true}
                title="Wettkampf importieren"
                width={700}
                height={600}
            >
                <div className="competition-import-dialog">
                    {renderStepContent()}
                    
                    <div className="dialog-footer">
                        {step === 1 && (
                            <>
                                <Button
                                    text="Abbrechen"
                                    onClick={handleHiding}
                                />
                                <Button
                                    text="Analysieren"
                                    type="success"
                                    onClick={handleAnalyze}
                                    disabled={!selectedFile}
                                />
                            </>
                        )}
                        
                        {step === 2 && (
                            <>
                                <Button
                                    text="Zurück"
                                    onClick={handleBack}
                                />
                                <Button
                                    text="Import starten"
                                    type="success"
                                    onClick={handleImport}
                                />
                            </>
                        )}
                    </div>
                </div>
            </Popup>

            <LoadPanel
                visible={isLoading}
                message={loadingText}
                showIndicator={true}
                showPane={true}
                shading={true}
            />
        </>
    );
}
